// src/features/live-board/components/TodaySoFarStrip.tsx
//
// «"today so far" board (due · pre-checked · not yet arrived · done, by
// hour)» (commercial-fit.md S5 differentiators). `board.today_so_far`
// resolves to a breakdown object, not a row list
// (`V2BoardFinItMetricDefinitions.BoardTodaySoFar`): `due`/`pre_checked`/
// `not_yet_arrived`/`done` counts plus a `done_by_hour` map this unit does
// not chart (CF S6's own rule for the board: "no chart to read" — the same
// convention `OperacionPage`'s five cards already follow, not repeated here).
import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { WidgetCard } from '@/components/common/WidgetCard';
import { DASH, formatCount } from '@/lib/analyticsFormat';
import { MetricStamp } from './MetricStamp';
import type { MetricResultDto } from '@/types/analyticsV2';

interface TodaySoFarStripProps {
  metric: MetricResultDto | undefined;
  loading: boolean;
}

const STAT_KEYS = ['due', 'pre_checked', 'not_yet_arrived', 'done'] as const;

export const TodaySoFarStrip: React.FC<TodaySoFarStripProps> = ({ metric, loading }) => {
  const translate = useTranslate();
  const value = metric && !metric.absent && !Array.isArray(metric.value) ? (metric.value as Record<string, unknown>) : null;

  return (
    <WidgetCard title={translate('resources.liveBoard.todaySoFar.title')}>
      {loading ? (
        <Skeleton height={48} />
      ) : (
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {STAT_KEYS.map((key) => (
            <Box key={key}>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {value && typeof value[key] === 'number' ? formatCount(value[key] as number) : DASH}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {translate(`resources.liveBoard.todaySoFar.${key}`)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {metric && <MetricStamp stamp={metric.stamp} />}
    </WidgetCard>
  );
};

export default TodaySoFarStrip;
